/**
 * Biconomy timeframe configuration and interval mapping.
 *
 * Biconomy REST candle `type` values mirror the production connector
 * (gitlab.1wq.net/mm/111-connman → connector/biconomy/common/enum.go):
 *   1min, 5min, 15min, 30min, hour, 4hour, day, week
 * (note: "hour"/"4hour"/"day"/"week" rather than "1h"/"4h"/"1d"/"1w").
 *
 * The WebSocket kline channel (kline.subscribe) instead uses an integer SECONDS interval.
 * `mapToBiconomyInterval` returns a canonical short label ('1m', '1h', ...) which the WS
 * factory maps to the corresponding seconds value.
 *
 * @see https://api.biconomy.com/api/v1/kline
 */

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/**
 * Biconomy supported kline intervals (canonical short labels).
 */
export const BICONOMY_TIMEFRAMES: TimeframeConfig = {
  native: ['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w'],
  synthetic: ['1s'], // Use ticker (state) stream for sub-minute
  minIntervalSeconds: 60,
};

// Register with central registry
registerExchangeTimeframes('biconomy', BICONOMY_TIMEFRAMES);

/**
 * Maps a TradingView resolution to a canonical Biconomy interval label.
 * (The WS factory converts the label to the integer SECONDS used by kline.subscribe.)
 */
export const mapToBiconomyInterval = (resolution: string): string => {
  const seconds = resolutionToSeconds(resolution);

  if (seconds <= 60) return '1m';
  if (seconds === 300) return '5m';
  if (seconds === 900) return '15m';
  if (seconds === 1800) return '30m';
  if (seconds === 3600) return '1h';
  if (seconds === 14400) return '4h';
  if (seconds === 86400) return '1d';
  if (seconds === 604800) return '1w';

  // Default to the closest smaller native interval
  if (seconds < 300) return '1m';
  if (seconds < 900) return '5m';
  if (seconds < 1800) return '15m';
  if (seconds < 3600) return '30m';
  if (seconds < 14400) return '1h';
  if (seconds < 86400) return '4h';
  if (seconds < 604800) return '1d';

  return '1w';
};
